// IVR Flows — form-based step builder (no JSON).
// Each flow has an ordered list of steps. Each step has a type + simple form fields.
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Workflow, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";

const STEP_TYPES = [
  { v: "play_prompt",   label: "Play prompt",        fields: ["audio_id"] },
  { v: "get_digits",    label: "Get digits (menu)",  fields: ["max_digits","timeout_seconds","options","invalid_no_input","exhausted"] },
  { v: "route_to_queue",label: "Route to queue",     fields: ["queue_id"] },
  { v: "route_to_ivr",  label: "Route to another IVR", fields: ["ivr_id"] },
  { v: "route_to_time_condition", label: "Route to time condition", fields: ["time_condition_id"] },
  { v: "voicemail",     label: "Voicemail",          fields: ["voicemail_id"] },
  { v: "external_xfer", label: "External transfer",  fields: ["external_number"] },
  { v: "hangup",        label: "Hang up",            fields: [] },
];

export default function IVRBuilderPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", max_attempts: 3, steps: [], is_active: true });
  const [saving, setSaving] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/resources/ivr_flows"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew() {
    setForm({ name: "", description: "", max_attempts: 3, is_active: true, steps: [{ id: id(), type: "play_prompt", audio_id: "" }] });
    setEditing("new");
  }
  function startEdit(r) {
    setForm({
      name: r.name, description: r.description||"", is_active: !!r.is_active,
      max_attempts: r.data?.max_attempts||3,
      steps: Array.isArray(r.data?.steps) && r.data.steps.length ? r.data.steps.map((s,i)=>({ id: s.id||id()+i, ...s })) : [{ id: id(), type: "play_prompt", audio_id: "" }],
    });
    setEditing(r);
  }
  function id() { return "s_" + Math.random().toString(36).slice(2,9); }
  function addStep() { setForm({ ...form, steps: [...form.steps, { id: id(), type: "play_prompt", audio_id: "" }] }); }
  function updateStep(i, patch) { setForm({ ...form, steps: form.steps.map((s,j)=> j===i ? { ...s, ...patch } : s) }); }
  function removeStep(i) { setForm({ ...form, steps: form.steps.filter((_,j)=>j!==i) }); }
  function moveStep(i, dir) {
    const arr = [...form.steps]; const t = i+dir;
    if (t<0||t>=arr.length) return;
    [arr[i], arr[t]] = [arr[t], arr[i]]; setForm({ ...form, steps: arr });
  }
  function addOption(i) { const opts = form.steps[i].options || []; updateStep(i, { options: [...opts, { key: String(opts.length+1), action: "queue", target_id: "" }] }); }
  function updateOption(i, oi, patch) { const opts = (form.steps[i].options||[]).map((o,k)=> k===oi ? { ...o, ...patch } : o); updateStep(i, { options: opts }); }
  function removeOption(i, oi) { const opts = (form.steps[i].options||[]).filter((_,k)=>k!==oi); updateStep(i, { options: opts }); }

  async function save() {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), description: form.description||"", is_active: !!form.is_active,
        data: { max_attempts: Number(form.max_attempts)||3, steps: form.steps },
      };
      if (editing === "new") await api.post("/admin/resources/ivr_flows", payload);
      else await api.patch(`/admin/resources/ivr_flows/${editing.id}`, payload);
      toast.success("IVR saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete IVR "${r.name}"?`)) return;
    try { await api.delete(`/admin/resources/ivr_flows/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="ivr-title">IVR Flows</h1>
          <p className="text-sm text-muted-foreground mt-1">Build menu trees step-by-step. Each step is a guided form — no code or JSON.</p>
        </div>
        {canWrite && <Button data-testid="add-ivr-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4"/> New IVR</Button>}
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Steps</th><th className="px-4 py-2">Active</th><th className="px-4 py-2 w-24">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-ivr-${r.id}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><Workflow className="w-4 h-4 text-primary"/><span className="font-medium">{r.name}</span></div></td>
                  <td className="px-4 py-2.5"><Badge variant="outline">{(r.data?.steps||[]).length}</Badge></td>
                  <td className="px-4 py-2.5">{r.is_active ? <Badge variant="outline" className="text-[10px]">On</Badge> : <Badge variant="destructive" className="text-[10px]">Off</Badge>}</td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">
                    {canWrite && <Button data-testid={`edit-ivr-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={()=>startEdit(r)}><Edit2 className="w-4 h-4"/></Button>}
                    {canDelete && <Button data-testid={`del-ivr-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>remove(r)}><Trash2 className="w-4 h-4"/></Button>}
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm text-muted-foreground">No IVR flows yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing==="new"?"New IVR":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>Add steps below. Calls run them top-to-bottom.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="ivr-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
              <div className="flex items-center gap-2 mt-7"><Switch data-testid="ivr-active" checked={!!form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:!!v})} /><Label>Active</Label></div>
              <div><Label>Max attempts</Label><Input data-testid="ivr-attempts" type="number" value={form.max_attempts} onChange={(e)=>setForm({...form, max_attempts:Number(e.target.value)})} /></div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold">Steps</h3>
                <Button data-testid="ivr-add-step" onClick={addStep} variant="outline" className="gap-1.5"><Plus className="w-4 h-4"/> Add step</Button>
              </div>
              <div className="space-y-2">
                {form.steps.map((s, i) => {
                  const meta = STEP_TYPES.find(t=>t.v===s.type) || STEP_TYPES[0];
                  return (
                    <Card key={s.id} className="p-3" data-testid={`ivr-step-${i}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{i+1}</Badge>
                        <Select value={s.type} onValueChange={(v)=>updateStep(i,{type:v, options: v==="get_digits"?[]:undefined})}>
                          <SelectTrigger data-testid={`ivr-step-type-${i}`} className="h-8 w-56"><SelectValue/></SelectTrigger>
                          <SelectContent>{STEP_TYPES.map(t=><SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="ml-auto flex items-center gap-1">
                          <Button onClick={()=>moveStep(i,-1)} variant="ghost" size="icon" className="h-7 w-7" data-testid={`ivr-up-${i}`}><ArrowUp className="w-3.5 h-3.5"/></Button>
                          <Button onClick={()=>moveStep(i,1)}  variant="ghost" size="icon" className="h-7 w-7" data-testid={`ivr-down-${i}`}><ArrowDown className="w-3.5 h-3.5"/></Button>
                          <Button onClick={()=>removeStep(i)}  variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`ivr-rm-${i}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                        </div>
                      </div>
                      <div className="mt-3 grid sm:grid-cols-2 gap-3">
                        {meta.fields.includes("audio_id") && (
                          <div className="sm:col-span-2"><Label>Audio prompt</Label>
                            <RelationalPicker kind="audio_prompts" value={s.audio_id||""} onChange={(v)=>updateStep(i,{audio_id:v})} testId={`ivr-step-audio-${i}`} placeholder="Pick a prompt…" />
                          </div>
                        )}
                        {meta.fields.includes("max_digits") && (
                          <div><Label>Max digits</Label><Input type="number" value={s.max_digits||1} onChange={(e)=>updateStep(i,{max_digits:Number(e.target.value)})} data-testid={`ivr-step-maxdig-${i}`} /></div>
                        )}
                        {meta.fields.includes("timeout_seconds") && (
                          <div><Label>Timeout (seconds)</Label><Input type="number" value={s.timeout_seconds||5} onChange={(e)=>updateStep(i,{timeout_seconds:Number(e.target.value)})} data-testid={`ivr-step-timeout-${i}`} /></div>
                        )}
                        {meta.fields.includes("queue_id") && (
                          <div className="sm:col-span-2"><Label>Queue</Label>
                            <RelationalPicker kind="queues" value={s.queue_id||""} onChange={(v)=>updateStep(i,{queue_id:v})} testId={`ivr-step-queue-${i}`} />
                          </div>
                        )}
                        {meta.fields.includes("ivr_id") && (
                          <div className="sm:col-span-2"><Label>Target IVR</Label>
                            <RelationalPicker kind="ivr_flows" value={s.ivr_id||""} onChange={(v)=>updateStep(i,{ivr_id:v})} testId={`ivr-step-ivr-${i}`} filter={(x)=>x.id!==editing?.id} />
                          </div>
                        )}
                        {meta.fields.includes("time_condition_id") && (
                          <div className="sm:col-span-2"><Label>Time Condition</Label>
                            <RelationalPicker kind="time_conditions" value={s.time_condition_id||""} onChange={(v)=>updateStep(i,{time_condition_id:v})} testId={`ivr-step-tc-${i}`} placeholder="Pick a time condition…" />
                          </div>
                        )}
                        {meta.fields.includes("voicemail_id") && (
                          <div className="sm:col-span-2"><Label>Voicemail box</Label>
                            <RelationalPicker kind="voicemail_boxes" value={s.voicemail_id||""} onChange={(v)=>updateStep(i,{voicemail_id:v})} testId={`ivr-step-vm-${i}`} />
                          </div>
                        )}
                        {meta.fields.includes("external_number") && (
                          <div className="sm:col-span-2"><Label>External number (E.164)</Label><Input value={s.external_number||""} onChange={(e)=>updateStep(i,{external_number:e.target.value})} data-testid={`ivr-step-ext-${i}`} /></div>
                        )}
                        {meta.fields.includes("options") && (
                          <div className="sm:col-span-2">
                            <div className="flex items-center justify-between mb-2"><Label>Key press options</Label><Button onClick={()=>addOption(i)} variant="outline" size="sm" className="gap-1" data-testid={`ivr-step-add-opt-${i}`}><Plus className="w-3 h-3"/>Add key</Button></div>
                            <div className="space-y-2">
                              {(s.options||[]).map((o, oi) => (
                                <div key={oi} className="grid grid-cols-[60px_140px_1fr_auto] gap-2 items-center" data-testid={`ivr-step-opt-${i}-${oi}`}>
                                  <Input placeholder="Key" value={o.key||""} onChange={(e)=>updateOption(i,oi,{key:e.target.value})} data-testid={`ivr-opt-key-${i}-${oi}`} />
                                  <Select value={o.action} onValueChange={(v)=>updateOption(i,oi,{action:v, target_id:""})}>
                                    <SelectTrigger className="h-9" data-testid={`ivr-opt-action-${i}-${oi}`}><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="queue">Queue</SelectItem>
                                      <SelectItem value="ivr">IVR</SelectItem>
                                      <SelectItem value="time_condition">Time Condition</SelectItem>
                                      <SelectItem value="voicemail">Voicemail</SelectItem>
                                      <SelectItem value="repeat">Repeat menu</SelectItem>
                                      <SelectItem value="hangup">Hang up</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <div>
                                    {o.action === "queue"     && <RelationalPicker kind="queues" value={o.target_id||""} onChange={(v)=>updateOption(i,oi,{target_id:v})} testId={`ivr-opt-target-${i}-${oi}`} />}
                                    {o.action === "ivr"       && <RelationalPicker kind="ivr_flows" value={o.target_id||""} onChange={(v)=>updateOption(i,oi,{target_id:v})} testId={`ivr-opt-target-${i}-${oi}`} filter={(x)=>x.id!==editing?.id} />}
                                    {o.action === "time_condition" && <RelationalPicker kind="time_conditions" value={o.target_id||""} onChange={(v)=>updateOption(i,oi,{target_id:v})} testId={`ivr-opt-target-${i}-${oi}`} />}
                                    {o.action === "voicemail" && <RelationalPicker kind="voicemail_boxes" value={o.target_id||""} onChange={(v)=>updateOption(i,oi,{target_id:v})} testId={`ivr-opt-target-${i}-${oi}`} />}
                                  </div>
                                  <Button onClick={()=>removeOption(i,oi)} variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`ivr-opt-rm-${i}-${oi}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                                </div>
                              ))}
                              {(s.options||[]).length === 0 && <div className="text-xs text-muted-foreground">No options yet — add one.</div>}
                            </div>
                          </div>
                        )}
                        {meta.fields.includes("invalid_no_input") && (
                          <>
                            <div className="sm:col-span-1"><Label>Invalid-input prompt</Label>
                              <RelationalPicker kind="audio_prompts" value={s.invalid_audio_id||""} onChange={(v)=>updateStep(i,{invalid_audio_id:v})} testId={`ivr-step-invalid-${i}`} placeholder="Optional — wrong key" />
                            </div>
                            <div className="sm:col-span-1"><Label>No-input prompt</Label>
                              <RelationalPicker kind="audio_prompts" value={s.no_input_audio_id||""} onChange={(v)=>updateStep(i,{no_input_audio_id:v})} testId={`ivr-step-noinput-${i}`} placeholder="Optional — timeout" />
                            </div>
                          </>
                        )}
                        {meta.fields.includes("exhausted") && (
                          <div className="sm:col-span-2 border-t pt-2">
                            <Label className="text-xs text-muted-foreground">After {form.max_attempts||3} failed attempts</Label>
                            <div className="grid grid-cols-[180px_1fr] gap-2 mt-1">
                              <Select value={s.exhausted_action||"hangup"} onValueChange={(v)=>updateStep(i,{exhausted_action:v, exhausted_target_id:"", exhausted_external:""})}>
                                <SelectTrigger className="h-9" data-testid={`ivr-step-exhausted-${i}`}><SelectValue/></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="hangup">Hang up</SelectItem>
                                  <SelectItem value="queue">Route to queue</SelectItem>
                                  <SelectItem value="ivr">Route to IVR</SelectItem>
                                  <SelectItem value="time_condition">Time Condition</SelectItem>
                                  <SelectItem value="voicemail">Voicemail</SelectItem>
                                  <SelectItem value="external">External number</SelectItem>
                                </SelectContent>
                              </Select>
                              <div>
                                {s.exhausted_action === "queue"          && <RelationalPicker kind="queues" value={s.exhausted_target_id||""} onChange={(v)=>updateStep(i,{exhausted_target_id:v})} testId={`ivr-step-exh-target-${i}`} />}
                                {s.exhausted_action === "ivr"            && <RelationalPicker kind="ivr_flows" value={s.exhausted_target_id||""} onChange={(v)=>updateStep(i,{exhausted_target_id:v})} testId={`ivr-step-exh-target-${i}`} filter={(x)=>x.id!==editing?.id} />}
                                {s.exhausted_action === "time_condition" && <RelationalPicker kind="time_conditions" value={s.exhausted_target_id||""} onChange={(v)=>updateStep(i,{exhausted_target_id:v})} testId={`ivr-step-exh-target-${i}`} />}
                                {s.exhausted_action === "voicemail"      && <RelationalPicker kind="voicemail_boxes" value={s.exhausted_target_id||""} onChange={(v)=>updateStep(i,{exhausted_target_id:v})} testId={`ivr-step-exh-target-${i}`} />}
                                {s.exhausted_action === "external"       && <Input placeholder="+44 20 7946 0958" value={s.exhausted_external||""} onChange={(e)=>updateStep(i,{exhausted_external:e.target.value})} data-testid={`ivr-step-exh-ext-${i}`} />}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="ivr-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save IVR"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
